/**
 * What a proposed role costs.
 *
 * Mirrors `compensationFor` in `proxy-server/lib/salaryBandMatch.js`. Shared by the Org Planning
 * Overview's propose-a-role form and the Role Approvals page, so the figure somebody proposes
 * against and the figure somebody approves against cannot drift apart — they are the same
 * calculation, run on the proxy, and neither view does arithmetic of its own.
 */

/** Why there is no figure. Each one is a real state, not an error — see `UNAVAILABLE_LABELS`. */
export type CompensationUnavailable =
  | 'no_track_and_level'
  | 'unknown_role'
  | 'ambiguous_role'
  | 'no_band'
  | 'bands_unavailable'
  /** The viewer's Salary Bands access does not cover every band behind the figure — or no band
   * matched, which a proposer is deliberately not told apart (see `compensationFor`). */
  | 'restricted';

export interface RoleCompensation {
  /** The matched band's `Average`, in euros. Null when nothing matched. */
  average: number | null;
  /** The matched band's `OTE Avg`, when the band has one — a role with variable pay. */
  ote: number | null;
  /** The FTE the figures are priced at: the bands hold full-time pay, and a Working Student is
   * 0.5 (`plannedRoleFte` on the proxy). Optional — absent from a response cached before Nov 2026. */
  fte?: number;
  /** Which of the two the cost below was built on. OTE wins where it exists, because for a
   * commission role that is what the company expects to pay. */
  basis: 'salary' | 'ote' | null;
  /** Months of the current year the role is paid for, given its planned start. */
  monthsCharged: number | null;
  /** Salary at 120%, prorated over `monthsCharged`. */
  costUntilYearEnd: number | null;
  /** The year `costUntilYearEnd` runs to, so the label never has to guess. */
  year: number;
  /**
   * The role's first day falls in a LATER year than `year`.
   *
   * ⚠️ Load-bearing, not decoration. `monthsCharged === 0` covers two different situations — a role
   * starting next year, and one starting after 15 December this year — and this side never sees the
   * start date, so it cannot tell them apart. Without this both were captioned "starts after
   * <year>", which is plainly false of a 20 December start.
   */
  startsNextYear: boolean;
  unavailable: CompensationUnavailable | null;
}

/** German grouping, matching `SalaryBandsView` and `SalaryBandsTable` — and `formatEuro` on the
 * proxy, so the Slack announcement and these pages show one recognisable number. */
const EURO = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

export function formatEuro(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? EURO.format(Math.round(value)) : '—';
}

/**
 * What to say when there is no figure.
 *
 * Each names the fix rather than the fault: `unknown_role` and `no_band` are both one row in the
 * salary bands base away from being answered, and saying "not available" would leave the reader
 * with no way to tell that from an outage.
 */
export const UNAVAILABLE_LABELS: Record<CompensationUnavailable, string> = {
  no_track_and_level: 'Pick a track and level to see the salary band.',
  unknown_role: 'This role has no code in the salary bands base, so no band could be matched.',
  ambiguous_role:
    'This role is mapped to two different codes in the salary bands base, so no band could be ' +
    'matched. Remove the stale row from identifier_role.',
  no_band: 'No salary band exists for this role at this level.',
  bands_unavailable: 'The salary bands could not be read right now.',
  restricted:
    'No figure can be shown here with your Salary Bands access. The approvers see the band, if ' +
    'there is one.',
};

/**
 * The same four states in a few words, for the bulk list.
 *
 * `UNAVAILABLE_LABELS` names the fix, which is right in a panel and wrong repeated under twenty-five
 * rows at 11px — `ambiguous_role` alone is ~140 characters and would wrap to four lines per row.
 */
export const UNAVAILABLE_SHORT: Record<CompensationUnavailable, string> = {
  no_track_and_level: 'no track and level',
  unknown_role: 'no salary band code',
  ambiguous_role: 'salary band mapping is ambiguous',
  no_band: 'no salary band at this level',
  bands_unavailable: 'salary bands unavailable',
  restricted: 'not in your Salary Bands access',
};

/** True when there is a salary worth rendering. */
export function hasSalary(comp: RoleCompensation | null | undefined): boolean {
  return Boolean(comp && !comp.unavailable && (comp.average !== null || comp.ote !== null));
}

/**
 * The band salary as one string: the average, with the OTE beside it where the band has one.
 *
 * Both are shown rather than only the basis, because they answer different questions — the average
 * is what the band says the job pays, the OTE is what a commission role costs at target, and an
 * approver comparing two proposals needs to see which kind of role each one is.
 */
export function bandSalaryLabel(comp: RoleCompensation): string {
  // ⚠️ `hasSalary` passes on EITHER figure, so a band carrying an OTE and no average is reachable —
  // and formatting the average unconditionally printed a bare em-dash where the salary belongs,
  // inside a panel that otherwise looks confident.
  // A part-time role is priced at its FTE, so say so — "15.000 €" alone reads as the band's figure.
  const atFte = typeof comp.fte === 'number' && comp.fte < 1 ? ` at ${comp.fte} FTE` : '';
  if (comp.ote !== null) {
    return comp.average === null
      ? `OTE ${formatEuro(comp.ote)}${atFte}`
      : `${formatEuro(comp.average)} · OTE ${formatEuro(comp.ote)}${atFte}`;
  }
  return `${formatEuro(comp.average)}${atFte}`;
}

/**
 * The caption under the cost figure.
 *
 * ⚠️ A role starting next year costs nothing THIS year, which is a correct answer that reads as a
 * broken one. It is spelled out in words instead of being left as a bare €0.
 */
export function costCaption(comp: RoleCompensation): string {
  const basis = comp.basis === 'ote' ? 'OTE' : 'salary';
  if (comp.monthsCharged === null) return `120% of ${basis} — set a planned start date to prorate it`;
  // Two ways to reach zero, and they are different facts about the role. See `startsNextYear`.
  if (comp.monthsCharged === 0) {
    return comp.startsNextYear
      ? `Starts after ${comp.year} — nothing falls into this year`
      : `Starts after 15 Dec ${comp.year} — nothing charged this year`;
  }
  const months = comp.monthsCharged === 1 ? '1 month' : `${comp.monthsCharged} months`;
  return `120% of ${basis}, ${months} of ${comp.year}`;
}
